import React, { useState, useEffect } from 'react';
import { Patient, STATUS_CONFIG, SUB_STATUS_CONFIG, CONDITION_CONFIG, PatientStatus, SurgeryCondition, Hospital, DEFAULT_AD_SETTINGS } from '../types';
import * as storageService from '../services/storageService';
import * as cloudflareService from '../services/cloudflareService';
import DOMPurify from 'dompurify';
import Timeline from '../components/Timeline';
import { usePatientSync } from '../hooks/usePatientSync';
import { useHospitalSync } from '../hooks/useHospitalSync';

import { Search, HeartPulse, ShieldCheck, Bell, BellRing, BellOff, Info, ChevronLeft, MessageSquare, CheckCircle2, Star, ExternalLink, Send } from 'lucide-react';
import { Link, useLocation, useSearchParams } from 'react-router-dom';
import AdBanner from '../components/AdBanner';
import AdTicker from '../components/AdTicker';
import IosInstallAnimation from '../components/IosInstallAnimation';

const FamilyView: React.FC = () => {
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const hospitalName = searchParams.get('h');
  const [code, setCode] = useState('');
  const [activePatientId, setActivePatientId] = useState<string | null>(null);
  const [initialHospital, setInitialHospital] = useState<Hospital | undefined>(undefined);
  const [error, setError] = useState('');
  const [lastUpdated, setLastUpdated] = useState<Date>(new Date());
  const [privacyAccepted, setPrivacyAccepted] = useState(false);

  const { data: patient } = usePatientSync(activePatientId);
  const { data: syncedHospital } = useHospitalSync(patient?.hospitalId || initialHospital?.id);
  const hospital = syncedHospital || initialHospital;
  const [permission, setPermission] = useState<NotificationPermission>(
    'Notification' in window ? Notification.permission : 'default'
  );
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [showIosWarning, setShowIosWarning] = useState(false);

  // Review checkout state
  const [showReviewModal, setShowReviewModal] = useState(false);
  const [reviewRating, setReviewRating] = useState(0);
  const [reviewHover, setReviewHover] = useState(0);
  const [reviewSubmitted, setReviewSubmitted] = useState(false);
  const [feedbackStep, setFeedbackStep] = useState(false); // Step 2: textarea for 1-4★
  const [feedbackMessage, setFeedbackMessage] = useState('');
  const [feedbackSending, setFeedbackSending] = useState(false);
  const [showCopiedToast, setShowCopiedToast] = useState(false);

  useEffect(() => {
    // Check initial subscription status
    const checkSubscription = async () => {
      if ('serviceWorker' in navigator && 'PushManager' in window) {
        try {
          const registration = await navigator.serviceWorker.ready;
          const subscription = await registration.pushManager.getSubscription();
          setIsSubscribed(!!subscription);
        } catch (e) {
          console.error("Error checking subscription:", e);
        }
      }
    };
    checkSubscription();
  }, []);

  // Check for pre-filled code from Landing Page or URL code
  useEffect(() => {
    const urlCode = searchParams.get('code');
    if (urlCode) {
      setCode(urlCode);
      handleAutoSearch(urlCode);
    } else if (location.state && location.state.prefillCode) {
      const prefill = location.state.prefillCode;
      setCode(prefill);
      handleAutoSearch(prefill);
      window.history.replaceState({}, document.title);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location, searchParams]);

  // Fetch hospital data based on URL parameter on load
  useEffect(() => {
    const fetchInitialHospital = async () => {
      if (hospitalName) {
        try {
          // Intentar obtener por "handle" o buscar coincidencias de nombre.
          const hospitals = await storageService.getHospitals();
          // Match by handle or exact name
          let foundHospital = hospitals.find(h =>
            h.handle.toLowerCase() === hospitalName.toLowerCase() ||
            h.name.toLowerCase() === hospitalName.toLowerCase()
          );

          if (foundHospital) {
            setInitialHospital(foundHospital);
          }
        } catch (e) {
          console.error("Error fetching initial hospital data", e);
        }
      }
    };
    fetchInitialHospital();
  }, [hospitalName]);

  const urlB64ToUint8Array = (base64String: string) => {
    const padding = '='.repeat((4 - base64String.length % 4) % 4);
    const base64 = (base64String + padding)
      .replace(/\-/g, '+')
      .replace(/_/g, '/');
    const rawData = window.atob(base64);
    const outputArray = new Uint8Array(rawData.length);
    for (let i = 0; i < rawData.length; ++i) {
      outputArray[i] = rawData.charCodeAt(i);
    }
    return outputArray;
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    setShowCopiedToast(true);
    setTimeout(() => setShowCopiedToast(false), 3000);
  };

  const handleShareStatus = async () => {
    if (!patient || !hospital) return;

    // Usar el "handle" (ej. muguerza-sp) en lugar del nombre para URLs más limpias y sin '%20'
    const shareUrl = `https://hospital-status.com/family?h=${hospital.handle}`;
    const shareText = `Sigue en tiempo real el estatus de cirugía de nuestro paciente a través del portal oficial de *${hospital.name}*.\n\n🔐 *Código Clínico:* \`${patient.id}\`\n🏥 *Acceso Directo:* ${shareUrl}`;

    if (navigator.share) {
      try {
        await navigator.share({
          title: 'Estatus de Cirugía',
          text: shareText
        });
      } catch (err) {
        copyToClipboard(shareText);
      }
    } else {
      copyToClipboard(shareText);
    }
  };

  const requestNotifications = async () => {
    const isIOS = /iPhone|iPad|iPod/.test(navigator.userAgent);
    const isStandalone = window.matchMedia('(display-mode: standalone)').matches || (window.navigator as any).standalone;

    if (isIOS && !isStandalone) {
      setShowIosWarning(true);
      return;
    }

    if (!('Notification' in window) || !('serviceWorker' in navigator) || !patient) {
      alert("Este dispositivo o navegador no soporta notificaciones Web Push.");
      return;
    }
    const result = await Notification.requestPermission();
    setPermission(result);

    if (result === 'granted') {
      try {
        const registration = await navigator.serviceWorker.register('/sw.js');
        await navigator.serviceWorker.ready;

        // Ensure we are getting the public key from backend
        const baseUrl = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1'
          ? 'http://localhost:8787'
          : '';

        const res = await fetch(`${baseUrl}/api/subscriptions`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ action: 'get_vapid' })
        });

        const { publicKey } = await res.json();

        if (!publicKey) {
          alert("Error crítico: No se recibió la llave pública (VAPID) del servidor.");
          console.error("No VAPID public key received from backend");
          return;
        }

        const subscription = await registration.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: urlB64ToUint8Array(publicKey)
        });

        // Helper: ArrayBuffer → base64url (required by web-push)
        const arrayBufferToBase64url = (buffer: ArrayBuffer): string => {
          const bytes = new Uint8Array(buffer);
          let binary = '';
          for (let i = 0; i < bytes.length; i++) {
            binary += String.fromCharCode(bytes[i]);
          }
          return btoa(binary)
            .replace(/\+/g, '-')
            .replace(/\//g, '_')
            .replace(/=+$/, '');
        };

        // Send to backend
        const backendRes = await fetch(`${baseUrl}/api/subscriptions`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            patientId: patient.id,
            endpoint: subscription.endpoint,
            keys: {
              p256dh: arrayBufferToBase64url(subscription.getKey('p256dh')!),
              auth: arrayBufferToBase64url(subscription.getKey('auth')!)
            }
          })
        });

        if (!backendRes.ok) throw new Error(`El backend devolvió error: ${backendRes.status}`);

        setIsSubscribed(true);
      } catch (err: any) {
        alert("Error durante la suscripción: " + (err.message || String(err)));
        console.error('Error registrando Push:', err);
      }
    }
  };

  const unsubscribeNotifications = async () => {
    if (!('serviceWorker' in navigator) || !patient) return;
    try {
      const registration = await navigator.serviceWorker.ready;
      const subscription = await registration.pushManager.getSubscription();
      if (subscription) {
        // Send delete to backend
        const baseUrl = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1'
          ? 'http://localhost:8787'
          : '';

        await fetch(`${baseUrl}/api/subscriptions`, {
          method: 'DELETE',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            patientId: patient.id,
            endpoint: subscription.endpoint
          })
        });

        // Unsubscribe locally
        await subscription.unsubscribe();
        setIsSubscribed(false);
        setPermission('default'); // Reset UI state to allow re-subscribing safely
        console.log('Suscripción cancelada exitosamente');
      }
    } catch (err) {
      console.error('Error al cancelar la suscripción:', err);
    }
  };

  // Auto-refresh lastUpdated when patient query changes
  useEffect(() => {
    if (patient) {
        setLastUpdated(new Date());
    }
  }, [patient]);

  // Auto-trigger review modal when patient reaches RECOVERY
  useEffect(() => {
    if (!patient || patient.currentStatus !== PatientStatus.RECOVERY) return;
    const key = `review_shown_${patient.id}`;
    if (localStorage.getItem(key)) return; // Already shown
    // Small delay for a natural transition
    const timer = setTimeout(() => {
      setShowReviewModal(true);
      setReviewRating(0);
      setReviewHover(0);
      setReviewSubmitted(false);
      setFeedbackStep(false);
      setFeedbackMessage('');
    }, 1500);
    return () => clearTimeout(timer);
  }, [patient?.currentStatus, patient?.id]);



  const handleAutoSearch = async (searchCode: string) => {
    setError(''); // Clear previous error
    const found = await storageService.getPatientByCode(searchCode);
    if (found) {
      setActivePatientId(found.id);
      setError('');
    } else {
      setError('Código no encontrado. Verifique e intente nuevamente.');
      setActivePatientId(null);
    }
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    handleAutoSearch(code);
  };

  // Calculate Color for Progress Bar
  const getProgressColor = (status: PatientStatus) => {
    if (status === PatientStatus.SURGERY) return 'bg-white';
    if (status === PatientStatus.RECOVERY) return 'bg-white';
    return 'bg-white/50';
  };

  // Helper to mask name with initials
  const getPatientInitials = (name?: string) => {
    if (!name || name === 'Paciente Anónimo') return 'Paciente';
    return name
      .split(' ')
      .map(n => n[0])
      .join('.') + '.';
  };

  return (
    <div className="min-h-screen bg-med-bg flex flex-col font-sans">
      {/* Header */}
      <header className="bg-white shadow-sm border-b border-med-light py-4 px-6 sticky top-0 z-20">
        <div className="max-w-2xl mx-auto flex items-center justify-between">
          <div className="w-6 flex items-center justify-start">
            {patient && (
              <button onClick={() => { setActivePatientId(null); setCode(''); }} className="text-med-navy hover:text-med-accent transition-colors focus:outline-none" aria-label="Volver a buscar">
                <ChevronLeft className="w-6 h-6" />
              </button>
            )}
          </div>
          <div className="flex items-center gap-3">
            {hospital?.branding?.logoUrl && hospital.branding.logoUrl.trim() !== '' && hospital.branding.logoUrl !== 'null' ? (
              <img
                src={hospital.branding.logoUrl}
                alt="Logo Hospital"
                className="h-8 sm:h-10 w-auto object-contain"
                onError={(e) => {
                  (e.target as HTMLImageElement).style.display = 'none';
                  (e.target as HTMLImageElement).parentElement!.innerHTML = DOMPurify.sanitize(`<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="${hospital?.branding?.primaryColor || '#5B65DC'}" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="w-6 h-6 sm:w-8 sm:h-8"><path d="M19 14c1.49-1.46 3-3.21 3-5.5A5.5 5.5 0 0 0 16.5 3c-1.76 0-3 .5-4.5 2-1.5-1.5-2.74-2-4.5-2A5.5 5.5 0 0 0 2 8.5c0 2.3 1.5 4.05 3 5.5l7 7Z"/><path d="M12 5 9.04 9.2a2 2 0 0 0-.28 1.41l.36 1.76a2 2 0 0 0 1.96 1.6h3.84a2 2 0 0 0 1.96-1.6l.36-1.76a2 2 0 0 0-.28-1.41L12 5Z"/></svg><h1 class="text-lg sm:text-xl font-bold text-med-navy tracking-tight truncate max-w-[200px] sm:max-w-none ml-2">${hospital?.name || hospitalName || 'Hospital Status'}</h1>`);
                }}
              />
            ) : (
              <>
                <HeartPulse className="w-5 h-5 sm:w-6 sm:h-6 flex-shrink-0" style={{ color: hospital?.branding?.primaryColor || '#5B65DC' }} />
                <h1 className="text-lg sm:text-xl font-bold text-med-navy tracking-tight truncate max-w-[200px] sm:max-w-none">
                  {hospital?.name || hospitalName || 'Hospital Status Familia'}
                </h1>
              </>
            )}
          </div>
          <div className="w-6"></div> {/* Spacer */}
        </div>
      </header>

      <main className="flex-1 max-w-2xl mx-auto w-full px-6 py-8">

        {!patient ? (
          /* Search State */
          <div className="flex flex-col items-center justify-center h-[60vh]">
            <div className="bg-white p-8 rounded-3xl shadow-lg border border-med-light w-full max-w-md">
              <h2 className="text-xl font-bold text-med-navy mb-6 text-center">Rastreo de Paciente</h2>
              <form onSubmit={handleSearch} className="space-y-4">
                <div className="relative">
                  <input
                    type="text"
                    value={code}
                    onChange={(e) => setCode(e.target.value.toUpperCase())}
                    placeholder="CÓDIGO (Ej: PT1234)"
                    className="w-full pl-4 pr-4 py-4 text-center text-2xl font-mono font-bold border-2 border-med-light rounded-xl focus:border-med-accent focus:outline-none uppercase placeholder:text-slate-300 text-med-navy bg-med-bg"
                  />
                </div>
                {hospital?.preferences?.requirePrivacyNotice && (
                  <label className="flex items-start gap-3 text-xs text-slate-500 cursor-pointer text-left px-2">
                    <input
                      type="checkbox"
                      checked={privacyAccepted}
                      onChange={(e) => setPrivacyAccepted(e.target.checked)}
                      className="mt-0.5 w-4 h-4 rounded border-slate-300 transition-colors"
                      style={{ accentColor: hospital?.branding?.primaryColor || '#5B65DC' }}
                    />
                    <span>
                      Autorizo el tratamiento de datos y acepto el{' '}
                      <a href="#" className="underline font-bold text-med-navy hover:opacity-80 transition-opacity">Aviso de Privacidad</a>.
                    </span>
                  </label>
                )}

                <button
                  type="submit"
                  disabled={(hospital?.preferences?.requirePrivacyNotice && !privacyAccepted) || !code.trim()}
                  className="w-full text-white font-bold py-4 rounded-xl shadow-lg transition-all active:scale-95 flex items-center justify-center gap-2 hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed"
                  style={privacyAccepted && code.trim() ? { backgroundColor: hospital?.branding?.primaryColor || '#5B65DC', boxShadow: `0 10px 15px -3px ${hospital?.branding?.primaryColor || '#5B65DC'}40` } : { backgroundColor: '#CBD5E1' }}
                >
                  <Search className="w-5 h-5" />
                  Ver Estado
                </button>
              </form>
            </div>
            <div className="mt-8 flex items-center gap-2 text-med-navy/50 text-xs">
              <ShieldCheck className="w-4 h-4" />
              <span>Privacidad garantizada. Datos efímeros.</span>
            </div>
          </div>
        ) : (
          /* Result State */
          <div className="animate-fade-in">
            {/* DOCTOR BANNER */}
            {!!patient.isDoctorEnCamino && (
              <div className="bg-amber-50 border border-amber-200 p-5 rounded-3xl shadow-[0_10px_40px_-10px_rgba(251,191,36,0.3)] mb-6 animate-slide-down relative overflow-hidden">
                <div className="absolute top-0 right-0 w-32 h-32 bg-amber-400/10 rounded-full blur-3xl -mr-10 -mt-10"></div>
                <div className="flex gap-4 relative z-10">
                  <div className="mt-1">
                    <div className="bg-amber-400 p-3 rounded-2xl shadow-inner text-white">
                      <MessageSquare className="w-6 h-6 sm:w-8 sm:h-8" />
                    </div>
                  </div>
                  <div>
                    <h3 className="text-amber-900 font-black text-lg sm:text-xl mb-1 tracking-tight">El médico saldrá a informar</h3>
                    <p className="text-amber-800/80 text-sm font-medium leading-relaxed">
                      En unos minutos, el médico saldrá a la sala de espera para brindarle información de forma presencial.
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Main Status Card */}
            <div
              className={`p-8 rounded-3xl shadow-xl mb-8 text-center transition-all duration-500 border border-black/5 ${patient.currentStatus === PatientStatus.WAITING
                ? 'animate-gradient-breathe'
                : patient.currentStatus !== PatientStatus.SURGERY
                  ? (STATUS_CONFIG[patient.currentStatus]?.color || 'bg-slate-100')
                  : ''
                }`}
              style={
                patient.currentStatus === PatientStatus.WAITING
                  ? {
                    background: `radial-gradient(circle at 30% 40%, ${hospital?.branding?.primaryColor || '#5B65DC'}30 0%, transparent 50%), radial-gradient(circle at 70% 60%, ${hospital?.branding?.primaryColor || '#5B65DC'}20 0%, transparent 50%), #ffffff`,
                    backgroundSize: '200% 200%',
                  }
                  : patient.currentStatus === PatientStatus.SURGERY
                    ? { backgroundColor: hospital?.branding?.primaryColor || '#5B65DC' }
                    : undefined
              }
            >
              <h3 className={`uppercase tracking-widest text-xs font-bold mb-3 ${STATUS_CONFIG[patient.currentStatus]?.text || 'text-med-navy'} opacity-60`}>Estado Actual</h3>

              {/* Main Label */}
              <div className="mb-6">
                <h2 className={`text-2xl md:text-4xl font-black leading-tight mb-2 ${STATUS_CONFIG[patient.currentStatus]?.text || 'text-med-navy'}`}>
                  {patient.currentStatus === PatientStatus.SURGERY && patient.currentSubStatus
                    ? SUB_STATUS_CONFIG[patient.currentSubStatus]?.label || patient.currentSubStatus
                    : STATUS_CONFIG[patient.currentStatus]?.label || patient.currentStatus}
                </h2>
                {patient.name && patient.name !== 'Paciente Anónimo' && (
                  <p className={`text-sm font-bold opacity-60 uppercase tracking-widest ${STATUS_CONFIG[patient.currentStatus]?.text || 'text-med-navy'}`}>
                    Paciente: {getPatientInitials(patient.name)}
                  </p>
                )}
                {patient.procedureName && (
                  <p className={`text-xs font-medium opacity-50 mt-1 ${STATUS_CONFIG[patient.currentStatus]?.text || 'text-med-navy'}`}>
                    {patient.procedureName}
                  </p>
                )}
                {patient.doctorName && (
                  <p className={`text-xs font-medium opacity-40 mt-0.5 ${STATUS_CONFIG[patient.currentStatus]?.text || 'text-med-navy'}`}>
                    {patient.doctorName}
                  </p>
                )}
              </div>

              {/* Visual Progress Bar in Card */}
              <div className="w-full max-w-sm mx-auto">
                <div className={`flex justify-between text-xs font-bold mb-1 px-1 ${STATUS_CONFIG[patient.currentStatus]?.text || 'text-med-navy'} opacity-80`}>
                  <span>Progreso del Proceso</span>
                  <span>{patient.surgeryProgress}%</span>
                </div>
                <div className="h-3 w-full bg-black/10 rounded-full overflow-hidden backdrop-blur-sm">
                  <div
                    className={`h-full ${getProgressColor(patient.currentStatus)} transition-all duration-1000 ease-out`}
                    style={{ width: `${patient.surgeryProgress}%` }}
                  />
                </div>
              </div>

              {/* Condition Message */}
              {patient.currentStatus === PatientStatus.SURGERY && patient.currentCondition && patient.currentCondition !== SurgeryCondition.NORMAL && (
                <div className="mt-6 bg-black/20 backdrop-blur-md rounded-xl p-4 inline-block max-w-sm text-left border border-white/10">
                  <div className="flex items-start gap-3 text-white">
                    <Info className="w-5 h-5 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="font-bold text-sm mb-1">{CONDITION_CONFIG[patient.currentCondition].label}</p>
                      <p className="text-xs opacity-90 leading-snug">
                        {CONDITION_CONFIG[patient.currentCondition].message}
                      </p>
                    </div>
                  </div>
                </div>
              )}

              <div className="mt-8 flex flex-col items-center gap-3">
                <div className={`inline-flex items-center px-4 py-1.5 rounded-full ${STATUS_CONFIG[patient.currentStatus]?.text?.includes('white') ? 'bg-white/20 text-white' : 'bg-black/5 ' + (STATUS_CONFIG[patient.currentStatus]?.text || 'text-med-navy')} opacity-80`}>
                  <p className="text-xs font-mono font-medium">
                    Actualizado: {lastUpdated.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                  </p>
                </div>

                {/* Notification & Share Controls */}
                <div className="mt-4 flex flex-col items-center gap-3 animate-fade-in w-full max-w-sm">
                  <div className="flex w-full gap-3">
                    <button
                      onClick={isSubscribed ? unsubscribeNotifications : requestNotifications}
                      className={`flex-1 flex flex-col items-center justify-center gap-1.5 py-3 rounded-2xl transition-all active:scale-95 shadow-sm border ${isSubscribed
                        ? 'bg-emerald-50 text-emerald-600 border-emerald-200 hover:bg-emerald-100'
                        : 'bg-white text-med-navy border-slate-200 hover:bg-slate-50'
                        }`}
                    >
                      {isSubscribed ? (
                        <>
                          <BellRing className="w-5 h-5 text-emerald-500" />
                          <span className="text-[11px] font-bold uppercase tracking-wider text-emerald-700">Suscrito</span>
                        </>
                      ) : (
                        <>
                          <Bell className="w-5 h-5 opacity-70" />
                          <span className="text-[11px] font-bold uppercase tracking-wider opacity-90">Alertas Push</span>
                        </>
                      )}
                    </button>

                    <button
                      onClick={handleShareStatus}
                      className="flex-1 flex flex-col items-center justify-center gap-1.5 py-3 rounded-2xl transition-all active:scale-95 shadow-sm border bg-[#25D366]/10 text-[#128C7E] border-[#25D366]/20 hover:bg-[#25D366]/20"
                    >
                      <Send className="w-5 h-5 opacity-90" />
                      <span className="text-[11px] font-bold uppercase tracking-wider opacity-90">Compartir</span>
                    </button>
                  </div>
                  
                  {showCopiedToast && (
                    <div className="text-[11px] font-bold text-[#128C7E] bg-[#25D366]/20 border border-[#25D366]/30 px-4 py-1.5 rounded-full animate-fade-in w-full text-center">
                      ¡Link y Código Copiados! Listo para pegar.
                    </div>
                  )}

                  <span className="text-[10px] text-med-navy/50 font-medium tracking-wide">
                    * Activa las alertas para que tu celular suene cuando cambie de fase
                  </span>
                </div>
              </div>
            </div>

            {/* iOS PWA Warning */}
            {permission === 'default' && /iPhone|iPad|iPod/.test(navigator.userAgent) && (
              <div className="mt-4 mb-8 bg-blue-50 border border-blue-200 p-4 rounded-xl text-blue-800 text-xs font-medium">
                <p><strong>Nota para iPhone:</strong> Para recibir alertas con la pantalla bloqueada, debes añadir esta página a tu pantalla de inicio. (Toca el ícono Compartir <span><svg className="w-3 h-3 inline pb-0.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M4 12v8a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8"></path><polyline points="16 6 12 2 8 6"></polyline><line x1="12" y1="2" x2="12" y2="15"></line></svg></span> y selecciona "Añadir a inicio").</p>
              </div>
            )}

            {/* Timeline */}
            <Timeline patient={patient} />

            {/* Mobile Ads — Only shown after patient lookup, below timeline */}
            {/* NOTE: hideAdsDuringSurgery is intentionally ignored on mobile.        */}
            {/* The family member is already inside the patient view — showing a      */}
            {/* subtle ad here is contextual and doesn't interrupt their experience.  */}
            {/* That flag only applies to the TV board (public space, passive view).  */}
            {(() => {
              const adSettings = hospital?.preferences?.adSettings || DEFAULT_AD_SETTINGS;
              const adAssets = [
                  ...(hospital?.globalAdAssets || []),
                  ...(hospital?.preferences?.adAssets || [])
              ];
              const showMobileAds = adSettings.adsEnabled && adSettings.showAdsOnMobile;

              if (!showMobileAds || adAssets.length === 0) return null;

              // Track mobile ad impressions with 24h Cooldown
              const visibleAds = adAssets.filter(a => {
                if (!a.isActive || !(a.target === 'ALL' || a.target === 'MOBILE_ONLY')) return false;
                
                // Si el paciente está en quirófano, ocultar anuncios que no deban mostrarse
                if (patient?.currentStatus === PatientStatus.SURGERY && a.showDuringSurgery === false) {
                  return false;
                }
                
                return true;
              });
              
              if (hospital && visibleAds.length > 0) {
                const storageKey = 'hs_ads_last_seen';
                const lastSeenObj = JSON.parse(localStorage.getItem(storageKey) || '{}');
                const now = Date.now();
                const COOLDOWN_MS = 24 * 60 * 60 * 1000; // 24 hours

                const newAds = visibleAds.filter(a => {
                  const lastSeen = lastSeenObj[a.id] || 0;
                  return (now - lastSeen) > COOLDOWN_MS;
                });
                
                if (newAds.length > 0) {
                  const sessionHash = btoa(navigator.userAgent).slice(0, 12);
                  const events = newAds.map(a => ({
                    hospitalId: hospital!.id,
                    adId: a.id,
                    adType: a.type,
                    source: 'mobile',
                    eventType: 'impression',
                    sessionHash,
                  }));
                  fetch('/api/ads/track/batch', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ events }),
                  }).catch(() => {});
                  
                  newAds.forEach(a => {
                    lastSeenObj[a.id] = now;
                  });
                  localStorage.setItem(storageKey, JSON.stringify(lastSeenObj));
                }
              }

              return (
                <div className="mt-8 space-y-3 animate-fade-in">
                  {/* Mobile Ad Banner */}
                  {visibleAds.some(a => a.type === 'BANNER_TEXT' || a.type === 'BANNER_IMAGE') && (
                    <AdBanner
                      assets={visibleAds}
                      variant="mobile"
                      theme="light"
                      primaryColor={hospital?.branding?.primaryColor || '#5B65DC'}
                      hospitalId={hospital?.id}
                    />
                  )}

                  {/* Mobile Ticker */}
                  {adSettings.tickerEnabled && visibleAds.some(a => a.type === 'TICKER') && (
                    <AdTicker
                      assets={visibleAds}
                      speed="slow"
                      variant="mobile"
                      theme="light"
                      primaryColor={hospital?.branding?.primaryColor || '#5B65DC'}
                      hospitalId={hospital?.id}
                    />
                  )}
                </div>
              );
            })()}

            {/* Custom iOS Warning Modal (Animated Vector Version) */}
            {showIosWarning && (
              <div className="fixed inset-0 z-50 flex items-center justify-center p-6 bg-med-navy/40 backdrop-blur-sm animate-fade-in">
                <div className="bg-white w-full max-w-sm rounded-[2rem] shadow-2xl p-6 text-center animate-scale-in border border-med-light flex flex-col items-center">
                  
                  {/* Vector Animation (Replaces text instructions) */}
                  <IosInstallAnimation />

                  <h3 className="text-xl font-bold text-med-navy tracking-tight mb-2">Activar Alertas con Sonido</h3>
                  <p className="text-med-navy/60 text-sm mb-6 leading-relaxed">
                    Sigue esta animación para instalar la App en tu pantalla de inicio y no perderte ninguna actualización.
                  </p>
                  
                  <button
                    onClick={() => setShowIosWarning(false)}
                    className="w-full font-bold bg-slate-100 hover:bg-slate-200 text-med-navy rounded-xl py-3.5 transition-colors"
                  >
                    Lo haré ahora
                  </button>
                </div>
              </div>
            )}

            {/* ===== REVIEW CHECKOUT MODAL ===== */}
            {showReviewModal && (
              <div className="fixed inset-0 z-50 flex items-center justify-center p-6 bg-med-navy/40 backdrop-blur-sm animate-fade-in">
                <div className="bg-white w-full max-w-sm rounded-[2rem] shadow-2xl p-8 text-center animate-scale-in border border-med-light">
                  {!reviewSubmitted && !feedbackStep ? (
                    <>
                      {/* Star icon */}
                      <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-5" style={{ backgroundColor: `${hospital?.branding?.primaryColor || '#5B65DC'}15` }}>
                        <Star className="w-8 h-8" style={{ color: hospital?.branding?.primaryColor || '#5B65DC' }} fill={hospital?.branding?.primaryColor || '#5B65DC'} />
                      </div>
                      <h3 className="text-xl font-black text-med-navy mb-2 tracking-tight">¿Cómo fue tu experiencia?</h3>
                      <p className="text-med-navy/60 text-sm mb-6 leading-relaxed">Tu opinión nos ayuda a seguir mejorando el servicio</p>

                      {/* Interactive Stars */}
                      <div className="flex justify-center gap-2 mb-8">
                        {[1, 2, 3, 4, 5].map((star) => (
                          <button
                            key={star}
                            onMouseEnter={() => setReviewHover(star)}
                            onMouseLeave={() => setReviewHover(0)}
                            onClick={() => setReviewRating(star)}
                            className="transition-all duration-200 active:scale-90 focus:outline-none"
                            style={{ transform: `scale(${(reviewHover || reviewRating) >= star ? 1.15 : 1})` }}
                          >
                            <Star
                              className="w-11 h-11 transition-colors duration-200"
                              fill={(reviewHover || reviewRating) >= star ? '#FBBF24' : 'transparent'}
                              stroke={(reviewHover || reviewRating) >= star ? '#FBBF24' : '#CBD5E1'}
                              strokeWidth={1.5}
                            />
                          </button>
                        ))}
                      </div>

                      {/* Submit button */}
                      <button
                        onClick={async () => {
                          if (reviewRating === 0) return;
                          localStorage.setItem(`review_shown_${patient.id}`, 'true');
                          if (reviewRating >= (hospital?.preferences?.googleReviewThreshold || 5)) {
                            // Alta calificación → save to DB + thank-you + Google Reviews
                            cloudflareService.submitFeedback({
                              hospitalId: hospital!.id,
                              rating: reviewRating,
                              patientCode: patient.id,
                            });
                            setReviewSubmitted(true);
                            if (hospital?.preferences?.googleReviewUrl) {
                              // Track click to Google Reviews
                              cloudflareService.trackReviewEvent({
                                hospitalId: hospital.id,
                                patientId: patient.id,
                                eventType: 'google_review_click',
                                rating: reviewRating,
                              });
                              setTimeout(() => {
                                window.open(hospital.preferences!.googleReviewUrl!, '_blank');
                              }, 800);
                            }
                          } else {
                            // Baja calificación → show feedback textarea step
                            setFeedbackStep(true);
                          }
                        }}
                        disabled={reviewRating === 0}
                        className={`w-full font-bold py-4 rounded-xl transition-all active:scale-95 flex items-center justify-center gap-2 ${reviewRating > 0
                          ? 'text-white shadow-lg hover:opacity-90'
                          : 'bg-slate-100 text-slate-300 cursor-not-allowed'
                          }`}
                        style={reviewRating > 0 ? { backgroundColor: hospital?.branding?.primaryColor || '#5B65DC', boxShadow: `0 10px 15px -3px ${hospital?.branding?.primaryColor || '#5B65DC'}40` } : undefined}
                      >
                        Enviar Valoración
                      </button>

                      {/* Skip */}
                      <button
                        onClick={() => {
                          localStorage.setItem(`review_shown_${patient.id}`, 'true');
                          // Track review dismissed
                          if (hospital) {
                            cloudflareService.trackReviewEvent({
                              hospitalId: hospital.id,
                              patientId: patient.id,
                              eventType: 'review_dismissed',
                            });
                          }
                          setShowReviewModal(false);
                        }}
                        className="mt-4 text-med-navy/30 text-xs font-medium hover:text-med-navy/50 transition-colors"
                      >
                        Omitir
                      </button>
                    </>
                  ) : feedbackStep && !reviewSubmitted ? (
                    /* Feedback textarea step (1-4★) */
                    <div className="animate-fade-in">
                      <div className="w-14 h-14 rounded-full flex items-center justify-center mx-auto mb-4" style={{ backgroundColor: `${hospital?.branding?.primaryColor || '#5B65DC'}12` }}>
                        <MessageSquare className="w-7 h-7" style={{ color: hospital?.branding?.primaryColor || '#5B65DC' }} />
                      </div>
                      <h3 className="text-lg font-black text-med-navy mb-1.5 tracking-tight">¿Qué podemos mejorar?</h3>
                      <p className="text-med-navy/50 text-xs mb-5 leading-relaxed">Tu comentario es privado y nos ayuda a mejorar</p>
                      <textarea
                        value={feedbackMessage}
                        onChange={(e) => setFeedbackMessage(e.target.value)}
                        maxLength={500}
                        rows={3}
                        placeholder="Cuéntanos tu experiencia..."
                        className="w-full bg-slate-50 border border-slate-200 rounded-xl p-4 text-sm text-slate-700 resize-none focus:outline-none focus:ring-2 focus:ring-opacity-50 mb-1"
                        style={{ '--tw-ring-color': hospital?.branding?.primaryColor || '#5B65DC' } as any}
                        autoFocus
                      />
                      <p className="text-[10px] text-slate-300 text-right mb-4">{feedbackMessage.length}/500</p>
                      <button
                        onClick={async () => {
                          setFeedbackSending(true);
                          await cloudflareService.submitFeedback({
                            hospitalId: hospital!.id,
                            rating: reviewRating,
                            message: feedbackMessage.trim() || undefined,
                            patientCode: patient.id,
                          });
                          setFeedbackSending(false);
                          setReviewSubmitted(true);
                        }}
                        disabled={feedbackSending}
                        className="w-full font-bold py-3.5 rounded-xl text-white transition-all active:scale-95 flex items-center justify-center gap-2 disabled:opacity-60"
                        style={{ backgroundColor: hospital?.branding?.primaryColor || '#5B65DC', boxShadow: `0 8px 15px -3px ${hospital?.branding?.primaryColor || '#5B65DC'}30` }}
                      >
                        {feedbackSending ? (
                          <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                        ) : (
                          <><Send className="w-4 h-4" /> Enviar Comentario</>
                        )}
                      </button>
                      <button
                        onClick={() => {
                          // Track dismissed after seeing feedback step
                          if (hospital) {
                            cloudflareService.trackReviewEvent({
                              hospitalId: hospital.id,
                              patientId: patient?.id,
                              eventType: 'review_dismissed',
                            });
                          }
                          setReviewSubmitted(true);
                        }}
                        className="mt-3 text-med-navy/30 text-xs font-medium hover:text-med-navy/50 transition-colors"
                      >
                        Omitir
                      </button>
                    </div>
                  ) : (
                    /* Thank You State */
                    <div className="animate-fade-in">
                      <div className="w-16 h-16 bg-emerald-50 text-emerald-500 rounded-full flex items-center justify-center mx-auto mb-5">
                        <CheckCircle2 className="w-8 h-8" />
                      </div>
                      <h3 className="text-xl font-black text-med-navy mb-2 tracking-tight">¡Gracias por tu opinión!</h3>
                      {reviewRating >= (hospital?.preferences?.googleReviewThreshold || 5) && hospital?.preferences?.googleReviewUrl ? (
                        <>
                          <p className="text-med-navy/60 text-sm mb-4 leading-relaxed">
                            Nos alegra que hayas tenido una buena experiencia.
                            Se abrirá una ventana para que puedas compartir tu reseña.
                          </p>
                          <div className="flex items-center justify-center gap-1.5 text-xs text-med-navy/40">
                            <ExternalLink className="w-3.5 h-3.5" />
                            <span>Redirigiendo a Google Reviews...</span>
                          </div>
                        </>
                      ) : reviewRating >= (hospital?.preferences?.googleReviewThreshold || 5) ? (
                        <p className="text-med-navy/60 text-sm leading-relaxed">
                          ¡Nos alegra mucho que hayas tenido una excelente experiencia! Gracias por confiar en nosotros.
                        </p>
                      ) : (
                        <p className="text-med-navy/60 text-sm leading-relaxed">
                          Tu retroalimentación es muy valiosa. Trabajaremos para mejorar constantemente.
                        </p>
                      )}
                      <button
                        onClick={() => setShowReviewModal(false)}
                        className="mt-6 w-full font-bold bg-slate-100 hover:bg-slate-200 text-med-navy rounded-xl py-3.5 transition-colors"
                      >
                        Cerrar
                      </button>
                    </div>
                  )}
                </div>
              </div>
            )}

            <button
              onClick={() => setActivePatientId(null)}
              className="mt-12 w-full text-med-navy/40 text-sm hover:text-med-accent font-medium transition-colors"
            >
              Consultar otro código
            </button>

          </div>
        )}
      </main>
    </div>
  );
};

export default FamilyView;